<template>
  <div>
    <section>
      <template>
        <v-container fluid>
          <!-- Last Calculation Info -->
          <v-row v-if="lastCalculationDate">
            <v-col cols="12">
              <v-alert dense elevation="2" class="mb-0">
                <div class="d-flex align-center">
                  <v-icon small left>mdi-clock-outline</v-icon>
                  <span class="text-caption">
                    <strong>Letzte Aktualisierung:</strong>
                    {{ formatCalculationDate(lastCalculationDate) }}
                  </span>
                </div>
              </v-alert>
            </v-col>
          </v-row>
          <!-- Compact Status Cards -->
          <v-row class="match-height">
            <!-- Green Status Count -->
            <v-col cols="4" sm="4">
              <v-card>
                <v-card-text class="py-2">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="text-caption grey--text text-uppercase">
                        Grün
                      </div>
                      <div class="text-h5 font-weight-bold success--text">
                        {{ trafficLightStats.green }}
                      </div>
                      <div class="text-caption">
                        {{ trafficLightStats.greenPercent }}%
                      </div>
                    </div>
                    <v-icon size="32" color="success">
                      mdi-check-circle
                    </v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Yellow Status Count -->
            <v-col cols="4" sm="4">
              <v-card>
                <v-card-text class="py-2">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="text-caption grey--text text-uppercase">
                        Gelb
                      </div>
                      <div class="text-h5 font-weight-bold orange--text">
                        {{ trafficLightStats.yellow }}
                      </div>
                      <div class="text-caption">
                        {{ trafficLightStats.yellowPercent }}%
                      </div>
                    </div>
                    <v-icon size="32" color="orange"> mdi-alert-circle </v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Red Status Count -->
            <v-col cols="4" sm="4">
              <v-card>
                <v-card-text class="py-2">
                  <div class="d-flex justify-space-between align-center">
                    <div>
                      <div class="text-caption grey--text text-uppercase">
                        Rot
                      </div>
                      <div class="text-h5 font-weight-bold error--text">
                        {{ trafficLightStats.red }}
                      </div>
                      <div class="text-caption">
                        {{ trafficLightStats.redPercent }}%
                      </div>
                    </div>
                    <v-icon size="32" color="error"> mdi-close-circle </v-icon>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Detailed View Section -->
          <v-row>
            <v-col cols="12">
              <v-card>
                <v-card-subtitle>
                  <v-row>
                    <v-col cols="12" sm="4" md="3">
                      <v-text-field
                        v-model="search"
                        outlined
                        append-icon="mdi-magnify"
                        label="Suchen"
                        hide-details="auto"
                        clearable
                        dense
                      ></v-text-field>
                    </v-col>
                    <v-col class="d-flex justify-end align-center">
                      <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn outlined text v-bind="attrs" v-on="on">
                            <span>
                              Gruppieren nach
                              <strong class="hidden-sm-and-down">{{
                                groupBySelector.text
                              }}</strong>
                              <v-icon small class="hidden-md-and-up">{{
                                groupBySelector.icon
                              }}</v-icon>
                            </span>
                            <v-icon right>mdi-menu-down</v-icon>
                          </v-btn>
                        </template>
                        <v-list nav>
                          <v-list-item-group
                            v-model="groupBySelector"
                            mandatory
                          >
                            <v-list-item
                              link
                              dense
                              :value="{
                                text: 'Ampelstatus',
                                value: 'groupByTrafficLight',
                                icon: 'mdi-traffic-light',
                              }"
                            >
                              <v-list-item-icon>
                                <v-icon dense>mdi-traffic-light</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title
                                >Ampelstatus
                              </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              link
                              dense
                              :value="{
                                text: 'Status',
                                value: 'groupByStatus',
                                icon: 'mdi-list-status',
                              }"
                            >
                              <v-list-item-icon>
                                <v-icon dense>mdi-list-status</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title>Status </v-list-item-title>
                            </v-list-item>
                            <v-list-item
                              link
                              dense
                              :value="{
                                text: 'Benutzergruppe',
                                value: 'groupByGroup',
                                icon: 'mdi-account-group',
                              }"
                            >
                              <v-list-item-icon>
                                <v-icon dense>mdi-account-group</v-icon>
                              </v-list-item-icon>
                              <v-list-item-title
                                >Benutzergruppe</v-list-item-title
                              >
                            </v-list-item>
                          </v-list-item-group>
                        </v-list>
                      </v-menu>
                      <v-btn-toggle
                        dense
                        v-model="helpers.displayMode"
                        mandatory
                        class="ml-2"
                      >
                        <v-btn outlined icon value="list">
                          <v-icon
                            :color="$vuetify.theme.dark ? '' : 'grey darken-1'"
                            >mdi-view-sequential-outline</v-icon
                          >
                        </v-btn>
                        <v-btn depressed outlined icon value="table">
                          <v-icon
                            :color="$vuetify.theme.dark ? '' : 'grey darken-1'"
                            >mdi-table</v-icon
                          >
                        </v-btn>
                      </v-btn-toggle>
                    </v-col>
                  </v-row>
                </v-card-subtitle>
                <v-divider v-if="helpers.displayMode === 'table'"></v-divider>
                <performance-targets-results-list
                  v-if="helpers.displayMode === 'list'"
                  :results="performanceTargetsWithResults"
                  :search="search"
                  :group-by-selector="groupBySelector"
                ></performance-targets-results-list>
                <performance-targets-results-data-table
                  v-else-if="helpers.displayMode === 'table'"
                  :results="performanceTargetsWithResults"
                  :search="search"
                  :group-by-selector="groupBySelector"
                ></performance-targets-results-data-table>
              </v-card>
            </v-col>
          </v-row>
        </v-container>
      </template>
    </section>
    <section v-if="checkIfSupport">
      <support-tools :sources="code"></support-tools>
    </section>
  </div>
</template>

<script>
// import { db, Timestamp } from "@/firebase";
import { ADMIN, MEMBER_MANAGEMENT } from "@/store/modules.js";
import {
  GET_USER_LIST,
  GET_PERFORMANCE_TARGETS,
  GET_PERFORMANCE_TARGET_RESULTS,
} from "@/store/action-types.js";
import SupportTools from "@/components/_system/helpers/SupportTools.vue";
import PerformanceTargetsResultsList from "@/components/member-management/performance-targets/PerformanceTargetsResultsList.vue";
import PerformanceTargetsResultsDataTable from "@/components/member-management/performance-targets/PerformanceTargetsResultsDataTable.vue";
export default {
  name: "member-management-performance-targets-matrix",
  components: {
    SupportTools,
    PerformanceTargetsResultsList,
    PerformanceTargetsResultsDataTable,
  },
  data() {
    return {
      helpers: {
        initialLoading: true,
        displayMode: "list",
      },
      groupBySelector: {
        text: "Ampelstatus",
        value: "groupByTrafficLight",
        icon: "mdi-traffic-light",
      },
      search: "",
    };
  },
  computed: {
    performanceTargets() {
      return this.$store.state.memberManagement.performanceTargets;
    },
    performanceTargetResults() {
      // this contains per user one result document with the following structure:
      // {
      //   user: { uid: string },
      //   totalPerformanceTargetStatus: "string"
      //   performanceTargetResults: [ { performanceTarget: { id: "string"}, value: number, status: "string"} ]
      // }

      return this.$store.state.memberManagement.performanceTargetResults;
    },
    performanceTargetsWithResults() {
      return this.memberships.map((membership) => {
        const result = this.performanceTargetResults.find(
          (result) => result.user.uid === membership.user.uid
        );

        const performanceTargetResults = {};
        if (result) {
          result.performanceTargets.forEach((targetResult) => {
            performanceTargetResults[targetResult.performanceTarget.id] = {
              ...targetResult,
            };
          });
        }

        const trafficLightStatus = result
          ? result.trafficLightStatus
          : "unknown";
        const totalStatus = result ? result.totalStatus : "unknown";

        // Sort order: green -> yellow -> red
        const trafficLightSortKey =
          trafficLightStatus === "green"
            ? "1"
            : trafficLightStatus === "yellow"
            ? "2"
            : trafficLightStatus === "red"
            ? "3"
            : "4";

        // Sort order: met -> notMet -> others
        const statusSortKey =
          totalStatus === "met" ? "1" : totalStatus === "notMet" ? "2" : "3";

        return {
          ...membership,
          // Legacy status field for backward compatibility
          totalPerformanceTargetResultStatus: totalStatus,
          // New traffic light status field
          trafficLightStatus: trafficLightStatus,
          performanceTargetResults,
          groupByGroup:
            membership.group.sortKey.toString().padStart(4, "0") +
            "_" +
            membership.group.title,
          groupByStatus: statusSortKey + "_" + totalStatus,
          groupByTrafficLight: trafficLightSortKey + "_" + trafficLightStatus,
          // Display text for grouping headers
          groupByDisplayText: {
            groupByGroup: membership.group.title,
            groupByStatus: this.statusText(totalStatus),
            groupByTrafficLight: this.trafficLightText(trafficLightStatus),
          },
        };
      });
    },
    memberships() {
      return this.$store.state.admin.membershipsPublic;
    },
    // membershipsPrepared() {
    //   var range = {
    //     start: this.event.duration.start.timestampToDate,
    //     end: this.event.duration.end.timestampToDate,
    //   };
    //   var membershipsFiltered = this.memberships.filter((obj) => {
    //     return (
    //       new Date(obj.user.memberSinceToDate) <= new Date(range.start) && // All members that where members before or at the start of this event
    //       !(new Date(obj.user.memberUntilToDate) <= new Date(range.end)) && // All members that didn't leave before or at the event of this event
    //       obj.access.type === "member"
    //     );
    //   });

    //   var memberships = membershipsFiltered;
    //   // if (this.event.status !== "planned") {}
    //   const result = memberships.map((item) => {
    //     const container = {
    //       groupByGroup:
    //         item.group.sortKey.toString().padStart(4, "0") +
    //         "_" +
    //         item.group.title,
    //       groupByDivision:
    //         item.user.division.sortKey.toString().padStart(4, "0") +
    //         "_" +
    //         item.user.division.title,
    //       group: {
    //         title: item.group.title,
    //         sortKey: item.group.sortKey,
    //         id: item.group.id,
    //       },
    //       user: {
    //         displayName: item.user.displayName,
    //         uid: item.user.uid,
    //         // photoURL = item.user.photoURL,
    //         // position = item.user.dutyPosition,
    //       },
    //     };
    //     return container;
    //   });
    //   return result;
    // },

    // Traffic Light Statistics
    trafficLightStats() {
      const total = this.performanceTargetsWithResults.length || 1;
      const green = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "green"
      ).length;
      const yellow = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "yellow"
      ).length;
      const red = this.performanceTargetsWithResults.filter(
        (r) => r.trafficLightStatus === "red"
      ).length;

      return {
        green,
        yellow,
        red,
        greenPercent: Math.round((green / total) * 100),
        yellowPercent: Math.round((yellow / total) * 100),
        redPercent: Math.round((red / total) * 100),
      };
    },

    // Get the most recent calculation date from all results
    lastCalculationDate() {
      if (
        !this.performanceTargetResults ||
        this.performanceTargetResults.length === 0
      ) {
        return null;
      }

      const dates = this.performanceTargetResults
        .filter((r) => r.meta && r.meta.lastCalculatedAt)
        .map((r) => r.meta.lastCalculatedAt.toDate());

      if (dates.length === 0) return null;

      return new Date(Math.max(...dates));
    },

    checkIfSupport() {
      return this.$store.getters["organization/checkIfSupport"];
    },
    code() {
      return [
        {
          title: "performanceTargetsWithResults",
          data: this.performanceTargetsWithResults,
        },
        { title: "memberships", data: this.memberships },
      ];
    },
  },
  created() {
    this.initialize();
  },
  methods: {
    async initialize() {
      // Get today's date (we'll pass the Date object, not milliseconds)
      // const today = new Date();

      await this.$store.dispatch(`${ADMIN}${GET_USER_LIST}`, {
        organizationId: this.$route.params.organizationId,
        queryIsFiltered: true,
        query: {
          type: "active",
        },
      });

      await this.$store.dispatch(
        `${MEMBER_MANAGEMENT}${GET_PERFORMANCE_TARGETS}`,
        {
          organizationId: this.$route.params.organizationId,
        }
      );

      await this.$store.dispatch(
        `${MEMBER_MANAGEMENT}${GET_PERFORMANCE_TARGET_RESULTS}`,
        {
          organizationId: this.$route.params.organizationId,
        }
      );

      setTimeout(() => {
        this.helpers.initialLoading = false;
      }, 1);
    },
    sortedTeamMembers(userGroup) {
      return this.memberships
        .filter((member) => member.group.id === userGroup.id)
        .sort((a, b) => a.user.displayName.localeCompare(b.user.displaName));
    },
    statusText(statusId) {
      switch (statusId) {
        case "met":
          return "Zielvorgabe erfüllt";
        case "notMet":
          return "Zielvorgabe nicht erfüllt";
        case "metByIndividualRuling":
          return "Zielvorgabe erfüllt (EA)";
        case "notMetByIndividualRuling":
          return "Zielvorgabe nicht erfüllt (EA)";
        case "fullySuspended":
          return "Zielvorgabe vollständig ausgesetzt";
        case "notStarted":
          return "Zielvorgabe nicht begonnen";
        case "temporarilySuspended":
          return "Zielvorgabe zeitweise ausgesetzt";
        case "individualRulingRequired":
          return "Einzelfallentscheidung erforderlich";
        case "notApplicable":
          return "Zielvorgabe nicht anwendbar";
        case "unknown":
          return "Zielvorgabe unbekannt";
        default:
          return "Fehler";
      }
    },
    trafficLightText(statusId) {
      switch (statusId) {
        case "green":
          return "Grüne Ampel";
        case "yellow":
          return "Gelbe Ampel";
        case "red":
          return "Rote Ampel";
        case "unknown":
          return "Unbekannt";
        default:
          return "Fehler";
      }
    },
    getPerformanceTargetStatus(performanceTarget, user) {
      const performanceTargetResults = performanceTarget.matchedResults;

      return performanceTargetResults.find(
        (result) => result.user.uid === user.user.uid
      );
      // from performanceTargetResults
      // const emptyDeadlinesStatus = {
      //   meta: {
      //     uid: user.user.uid,
      //     lastUpdatedBy: "",
      //   },
      //   status: {
      //     id: "0",
      //   },
      // };
      // console.log("Deadline:", deadline);

      // const response = performanceTarget.responses.find(
      //   (response) => response.meta.uid === user.user.uid
      // );

      // if (!response) {
      //   emptyDeadlinesStatus.status.id = "-1";
      // }

      // return response ? response : emptyDeadlinesStatus;
      // return emptyDeadlinesStatus;
    },
    getSummaryCount() {
      // const possibleUsers = this.memberships.filter((user) => {
      //   return (
      //     this.isUserInvited(event, user) && this.isUserActive(event, user)
      //   );
      // });
      // const possibleUsersWithoutResponses = possibleUsers.filter((user) => {
      //   return !event.responses.find(
      //     (response) => response.meta.uid === user.user.uid
      //   );
      // });
      // return {
      //   totalResponses: event.responses.length,
      //   totalConfirmations: event.responses.filter(
      //     (response) => response.status.id === "1"
      //   ).length,
      //   totalCancellations: event.responses.filter(
      //     (response) => response.status.id === "2"
      //   ).length,
      //   totalIfNecessary: event.responses.filter(
      //     (response) => response.status.id === "3"
      //   ).length,
      //   totalNotInTown: event.responses.filter(
      //     (response) => response.status.id === "4"
      //   ).length,
      //   totalAwaitingResponse: possibleUsersWithoutResponses.length,
      // };
    },
    formatCalculationDate(date) {
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      // Format the date and time
      const dateStr = date.toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      const timeStr = date.toLocaleTimeString("de-DE", {
        hour: "2-digit",
        minute: "2-digit",
      });

      // Add relative time
      let relativeTime = "";
      if (diffMins < 1) {
        relativeTime = "gerade eben";
      } else if (diffMins < 60) {
        relativeTime = `vor ${diffMins} Minute${diffMins !== 1 ? "n" : ""}`;
      } else if (diffHours < 24) {
        relativeTime = `vor ${diffHours} Stunde${diffHours !== 1 ? "n" : ""}`;
      } else if (diffDays === 1) {
        relativeTime = "gestern";
      } else if (diffDays < 7) {
        relativeTime = `vor ${diffDays} Tagen`;
      }

      return relativeTime
        ? `${dateStr} um ${timeStr} Uhr (${relativeTime})`
        : `${dateStr} um ${timeStr} Uhr`;
    },
  },
};
</script>
